import axios, { AxiosResponse, AxiosRequestConfig, AxiosError } from 'axios'
import { REFRESH_TOKEN } from 'services/auth/CONSTANTS'
import Storage, { Auth } from 'storage/auth'
import { IApiResponse } from 'types'
import * as toast from 'services/context/toast'
import { checkIsHDAByStorage } from 'utils/hda'

// Axios client without baseURL - works with absolute URLs from all services
const client = axios.create()

export interface IRequestOptions extends AxiosRequestConfig {
  removeBearer?: boolean
  disableToast?: boolean
  removeTimezone?: boolean
  removeHDAFlag?: boolean
}

interface IBusinessRules {
  [index: number | string]: {
    message?: string
    action?: () => void
  }
}

const BusinessRules: IBusinessRules = {
  400: {},
  403: {
    message: 'Ação não permitida.'
  },
  // // Trying remove this to default logout
  // 401: {
  //   message: 'Sessão Expirada. Redirecionando ao login...',
  //   action: () => {
  //     setTimeout(() => {
  //       Auth.logout()
  //     }, 3000)
  //   }
  // },
  500: {
    message: 'Ops... Algo deu errado, tente novamente!'
  }
}

const arrayErrors = (message: any) => {
  if (Array.isArray(message)) {
    const errorMessages: any[] = message?.flatMap((m: any) => m?.messages)
    if (Array.isArray(errorMessages)) {
      if (errorMessages[0]) {
        return errorMessages.reduce((acc, cur) => `${acc}` + cur.message, '')
      } else return
    }
    return errorMessages
  }
  return message
}

export const request = async <T>(
  options: IRequestOptions
): Promise<IApiResponse<T>> => {
  const { removeBearer, disableToast, removeTimezone, removeHDAFlag } = options
  const isHDA = checkIsHDAByStorage()

  if (!removeBearer) {
    client.defaults.headers.common.Authorization = `Bearer ${Storage.token.get()}`
  }

  if (!removeTimezone) {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone

    client.defaults.headers.common['timezone'] = timezone
  }

  if (!removeHDAFlag) {
    client.defaults.headers.common['isHDA'] = isHDA
  }

  client.defaults.headers.common['content-type'] =
    `application/json; charset=utf-8`

  // Uploads multipart precisam que o browser defina o content-type junto com o
  // boundary. Se o application/json acima vazar para uma requisição com
  // FormData, o axios serializa o FormData como JSON e o arquivo é perdido
  // (vira `{"image":{}}`). Sobrescrevemos por requisição, sem mexer no default
  // compartilhado, para não afetar requisições concorrentes.
  const config: IRequestOptions =
    options.data instanceof FormData
      ? {
          ...options,
          headers: { ...options.headers, 'content-type': undefined }
        }
      : options

  const onSuccess = (response: AxiosResponse<T>) => {
    return {
      success: true,
      data: response.data,
      status: response.status
    }
  }

  const onError = (error: any) => {
    if (error.response) {
      if (error.response.status in BusinessRules) {
        const _businessRules = BusinessRules[error.response.status]
        const msg = arrayErrors(
          _businessRules.message ?? error.response.data.message
        )

        if (msg && typeof msg === 'string' && !disableToast) {
          toast.handler({
            content: msg,
            duration: 10000,
            severity: 'error'
          })
        }

        if (_businessRules.action) {
          _businessRules.action()
        }
      }
    } else if (error.request) {
      console.warn('request', error.request)
    } else {
      console.warn('Error', error.message)
    }

    if (Array.isArray(error?.response?.data?.message)) {
      const errorMessages: any[] = error?.response?.data?.message?.flatMap(
        (m: any) => m?.messages
      )

      return {
        success: false,
        data: error?.data,
        status: error?.response?.status,
        errors: errorMessages
      }
    }
    return {
      success: false,
      data: error?.data,
      errors: [error?.response?.data],
      status: error?.response?.status
    }
  }

  return await client(config).then(onSuccess).catch(onError)
}

let isRefreshing = false
let refreshSubscribers: Array<(token: string) => void> = []
let lastRefreshedToken: string | null = null

const subscribeTokenRefresh = (): Promise<string> => {
  return new Promise(resolve => {
    // If token was recently refreshed, use it immediately
    if (lastRefreshedToken) {
      resolve(lastRefreshedToken)
    } else {
      refreshSubscribers.push(resolve)
    }
  })
}

const onRefreshed = (newToken: string) => {
  lastRefreshedToken = newToken
  refreshSubscribers.forEach(callback => callback(newToken))
  refreshSubscribers = []
}

client.interceptors.response.use(
  response => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as AxiosRequestConfig & {
      _retry?: boolean
    }

    // if error is from refresh-token endpoint then logout
    if (error?.config?.url?.includes('/refresh-token')) {
      Auth.logout()
      throw error
    }

    if (error?.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true

      if (isRefreshing) {
        // Token refresh already in progress, queue this request
      } else {
        isRefreshing = true
        lastRefreshedToken = null // Reset the cached token when starting a new refresh

        try {
          const refresh_token = Storage.refreshToken.get() ?? ''

          if (!refresh_token) {
            throw error
          }

          const response = await client.post(REFRESH_TOKEN, { refresh_token })

          if (response.status !== 200) {
            Auth.logout()
            throw error
          }

          const newToken = response.data?.token ?? ''
          const newRefreshToken = response.data?.refresh_token ?? ''

          Storage.token.set(newToken)
          Storage.refreshToken.set(newRefreshToken)
          client.defaults.headers.common.Authorization = `Bearer ${newToken}`

          onRefreshed(newToken)
        } catch (refreshError) {
          isRefreshing = false
          lastRefreshedToken = null
          delete client.defaults.headers.common.Authorization
          Auth.logout()
          throw refreshError instanceof Error
            ? refreshError
            : new Error(String(refreshError))
        } finally {
          isRefreshing = false
        }
      }

      try {
        const newToken = await subscribeTokenRefresh()
        // Update the authorization header for the retry
        originalRequest.headers ??= {}
        originalRequest.headers.Authorization = `Bearer ${newToken}`
        // Retry the original request with the new token
        return client(originalRequest)
      } catch (subError) {
        throw subError instanceof Error ? subError : new Error(String(subError))
      }
    }

    throw error
  }
)
